/**
 * plansEntry.ts — where a visit to /plans came from.
 *
 * Every link into the plans page carries `?src=<entry>`, and the page sends
 * one `plans_viewed` PostHog event on opening with that `source` (or
 * 'direct' when there is none: a typed URL, a bookmark, the back button).
 * The same value rides on to the server as the checkout / top-up `source`
 * (routes/billing.js sourceLabel), so `checkout_started` says the same thing.
 * `from` was taken (the pause card's return path), hence `src`.
 *
 * The registry. One name per place in the app that shows plans:
 *
 *   sidebar_balance            the balance row / ring in the sidebar ("See plans",
 *                              or "Get more", which also carries topup=1)
 *   settings_billing           Settings -> Billing "See plans" (?org= for a team)
 *   settings_hosting           Settings -> Billing "Manage hosting"
 *   composer_hint              the credit line above the composer (home and chat)
 *   composer_notice            the "out of building" toast on the home page
 *   refusal_<feature>          a refusal note on a control: refusal_create_project,
 *                              refusal_import, refusal_saas_template,
 *                              refusal_zip_download, refusal_custom_domains
 *   lifecycle_banner_<reason>  the paused / archived banner on an app or task page:
 *                              lifecycle_banner_trial_ended, _plan_ended,
 *                              _template_trial_ended; plain lifecycle_banner for
 *                              the "See plans" beside a failed restore
 *   template_trial_banner      the "Template trial · 20h left" banner on a task
 *   pause_card                 the pause card in a chat (See plans / Upgrade / Add $10)
 *   publish_gate               the Publish popover's "See plans"
 *   forms_inbox                the forms inbox "this month's limit was reached"
 *   email_form_limit           server emails (services/formNotifyService,
 *   email_landing_pause        projectLifecycleService): the links in them
 *   email_apps_archive
 *   direct                     no src (reported by the page, never put in a link)
 *
 * Adding one: give the link `plansHref(PLANS_SRC.x, …)` (or `withPlansSrc`
 * on an href someone else built), add the name above and in
 * docs/ANALYTICS_ARCHITECTURE.md. Names are lowercase [a-z0-9_:-], at most 64
 * characters, the server's sourceLabel rule.
 */
import posthog from 'posthog-js';

export const PLANS_SRC_PARAM = 'src';
export const PLANS_SRC_DIRECT = 'direct';

export const PLANS_SRC = {
  sidebarBalance: 'sidebar_balance',
  settingsBilling: 'settings_billing',
  settingsHosting: 'settings_hosting',
  composerHint: 'composer_hint',
  composerNotice: 'composer_notice',
  lifecycleBanner: 'lifecycle_banner',
  templateTrialBanner: 'template_trial_banner',
  pauseCard: 'pause_card',
  publishGate: 'publish_gate',
  formsInbox: 'forms_inbox',
} as const;

const SRC_RE = /^[a-z0-9_:-]{1,64}$/i;

const snake = (s: string) => s.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();

/** A refusal note's entry name: refusal_create_project, refusal_zip_download… */
export function refusalSrc(feature: string): string {
  return `refusal_${snake(feature)}`;
}

/** A lifecycle banner's entry name, by why the app is paused or archived. */
export function lifecycleSrc(reason?: string | null): string {
  const r = typeof reason === 'string' ? snake(reason.trim()) : '';
  return r && SRC_RE.test(`${PLANS_SRC.lifecycleBanner}_${r}`) ? `${PLANS_SRC.lifecycleBanner}_${r}` : PLANS_SRC.lifecycleBanner;
}

export interface PlansHrefOptions {
  /** Whose balance (design D7: the org that owns the project). */
  project?: string | null;
  /** A team picked in Settings; ignored when `project` is given, as the page does. */
  org?: string | null;
  /** Focus that plan's card on arrival. */
  plan?: string | null;
  /** Open the top-up sheet on arrival. */
  topup?: boolean;
}

/** A link to the plans page from `src`; `null` src (the page linking to itself with no origin) adds no param. */
export function plansHref(src: string | null, { project = null, org = null, plan = null, topup = false }: PlansHrefOptions = {}): string {
  const q = new URLSearchParams();
  if (project) q.set('project', project);
  else if (org) q.set('org', org);
  if (plan) q.set('plan', plan);
  if (topup) q.set('topup', '1');
  if (src) q.set(PLANS_SRC_PARAM, src);
  const s = q.toString();
  return s ? `/plans?${s}` : '/plans';
}

/**
 * Stamp `src` onto an href someone else built (a `plansHref` option passed
 * down by a page). Only a /plans link, and only when it has none yet: an
 * entry that already named itself wins over a generic one downstream.
 */
export function withPlansSrc(href: string, src: string): string {
  const i = href.indexOf('?');
  const path = i < 0 ? href : href.slice(0, i);
  if (path !== '/plans') return href;
  const q = new URLSearchParams(i < 0 ? '' : href.slice(i + 1));
  if (q.has(PLANS_SRC_PARAM)) return href;
  q.set(PLANS_SRC_PARAM, src);
  return `/plans?${q.toString()}`;
}

/** The entry name in a /plans URL's query, sanitised (a link can say anything), or null. */
export function readPlansSrc(search: string): string | null {
  const v = new URLSearchParams(search).get(PLANS_SRC_PARAM);
  if (!v) return null;
  const t = v.trim();
  return SRC_RE.test(t) ? t.toLowerCase() : null;
}

/** Client-only PostHog, guarded like the tour's events: no key → no-op, never throws into the page. */
export function trackPlansViewed(props: Record<string, unknown>): void {
  if (!process.env.REACT_APP_POSTHOG_KEY) return;
  try {
    posthog.capture('plans_viewed', props);
  } catch {
    /* analytics must never break the page */
  }
}
